import { useMemo, useEffect, useCallback, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Users, TrendingUp, BarChart3, Loader2, Printer, Share2, Mail, Copy, Tag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getErrorMessage } from "@/utils/errors";
import { AnalysisTable } from "@/features/scripts/components/AnalysisTable";
import { useScriptAnalysis } from "@/features/scripts/hooks/useScriptAnalysis";
import { useAnalysisShare } from "@/features/scripts/hooks/useAnalysisShare";
import { useDeleteSubmission } from "@/features/scripts/hooks/useDeleteSubmission";
import { useScriptVersions } from "@/features/versions/hooks";
import type { AnalysisSubmission } from "@/features/scripts/types";
import type { ScriptForm, Script } from "@/features/dashboard/types";
import type { ScriptVersion } from "@/features/versions/types";
import { useTranslation } from "@/i18n/useTranslation";

interface ScriptAnalysisDialogProps {
  scriptForm: ScriptForm;
  scriptName: string;
  script: Script;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ScoreVariant = "default" | "secondary" | "destructive";

interface VersionSelectorProps {
  versions: ScriptVersion[] | undefined;
  activeVersion: ScriptVersion | undefined;
  activeVersionId: string;
  currentVersionId: string | null | undefined;
  onChange: (value: string) => void;
}

const VersionSelector = ({ versions, activeVersion, activeVersionId, currentVersionId, onChange }: VersionSelectorProps) => {
  if (!versions || versions.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 border-t pt-3">
      <Tag className="h-4 w-4 text-muted-foreground" />
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">Version:</span>
        <Select value={activeVersionId} onValueChange={onChange}>
          <SelectTrigger className="h-9 w-[200px] text-sm">
            <SelectValue>
              {activeVersion?.version_name
                ? `v${activeVersion.version_number}: ${activeVersion.version_name}`
                : `Version ${activeVersion?.version_number || 1}`}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {versions.map((version) => (
              <SelectItem key={version.id} value={version.id}>
                <div className="flex items-center gap-2">
                  <span>
                    v{version.version_number}
                    {version.version_name && `: ${version.version_name}`}
                  </span>
                  {version.id === currentVersionId && (
                    <Badge variant="outline" className="text-xs text-white">
                      Current
                    </Badge>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

interface ShareActionsProps {
  onPrint: () => void;
  onShare: () => void;
  onCopyLink: () => void;
  onEmail: () => void;
  canUseNativeShare: boolean;
}

const ShareActions = ({ onPrint, onShare, onCopyLink, onEmail, canUseNativeShare }: ShareActionsProps) => (
  <>
    <div className="flex flex-wrap gap-3 border-t pt-3">
      <Button variant="outline" size="sm" onClick={onPrint} className="h-8 px-3">
        <Printer className="mr-2 h-3 w-3" />
        Print / PDF
      </Button>
      <Button size="sm" onClick={onShare} className="h-8 px-3">
        <Share2 className="mr-2 h-3 w-3" />
        Share
      </Button>
      <Button variant="outline" size="sm" onClick={onCopyLink} className="h-8 px-3">
        <Copy className="mr-2 h-3 w-3" />
        Copy Link
      </Button>
      <Button variant="ghost" size="sm" onClick={onEmail} className="h-8 px-3">
        <Mail className="mr-2 h-3 w-3" />
        Email
      </Button>
    </div>
    {!canUseNativeShare && (
      <p className="text-xs text-muted-foreground">
        Device sharing isn't available here. Use Copy Link or Email instead.
      </p>
    )}
  </>
);

const AnalysisEmptyState = () => (
  <Card className="py-12 text-center">
    <CardContent>
      <Users className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
      <h3 className="mb-2 text-lg font-semibold text-foreground">No feedback yet</h3>
      <p className="text-muted-foreground">
        Share your review links to start collecting feedback for this script.
      </p>
    </CardContent>
  </Card>
);

interface AnalysisSummaryProps {
  analysisData: NonNullable<ReturnType<typeof useScriptAnalysis>["data"]>;
  scriptName: string;
  formDisplayName: string;
  formatDate: (date: string) => string;
  getScoreColor: (score: number) => string;
  getScoreBadgeVariant: (score: number) => ScoreVariant;
  calculateSubmissionScore: (submission: AnalysisSubmission) => number;
  deleteSubmission: (submissionId: string) => Promise<void>;
  isDeleting: boolean;
}

const AnalysisSummary = ({
  analysisData,
  scriptName,
  formDisplayName,
  formatDate,
  getScoreColor,
  getScoreBadgeVariant,
  calculateSubmissionScore,
  deleteSubmission,
  isDeleting,
}: AnalysisSummaryProps) => (
  <div className="space-y-6 print:space-y-0">
    <div className="grid grid-cols-1 gap-4 print:hidden md:grid-cols-3">
      <Card className="shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{analysisData.submissions.length}</div>
        </CardContent>
      </Card>

      <Card className="shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Questions</CardTitle>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{analysisData.questions.length}</div>
        </CardContent>
      </Card>

      <Card className="shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Overall Score</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${getScoreColor(analysisData.weightedOverallScore)}`}>
            {analysisData.weightedOverallScore.toFixed(1)}/5
          </div>
        </CardContent>
      </Card>
    </div>

    <Card className="shadow-soft print:hidden">
      <CardHeader>
        <CardTitle>Review Details</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <AnalysisTable
            analysisData={analysisData}
            formatDate={formatDate}
            getScoreBadgeVariant={getScoreBadgeVariant}
            calculateSubmissionScore={calculateSubmissionScore}
            onDeleteSubmission={deleteSubmission}
            isDeletingSubmission={isDeleting}
          />
        </div>
      </CardContent>
    </Card>

    <div className="hidden print:block print:px-4 print:pt-4">
      <h2 className="mb-4 text-lg font-semibold">
        Analysis: {scriptName} - {formDisplayName}
      </h2>
      <AnalysisTable
        analysisData={analysisData}
        formatDate={formatDate}
        getScoreBadgeVariant={getScoreBadgeVariant}
        calculateSubmissionScore={calculateSubmissionScore}
        onDeleteSubmission={deleteSubmission}
        isDeletingSubmission={isDeleting}
      />
    </div>
  </div>
);

const useSubmissionScoring = (analysisData: ReturnType<typeof useScriptAnalysis>["data"]) => {
  const hasWeightedQuestions = useMemo(() => {
    return analysisData?.questions.some((question) => question.weight_percentage > 0) ?? false;
  }, [analysisData]);

  const calculateSubmissionScore = useCallback(
    (submission: AnalysisSubmission) => {
      if (!analysisData) return 0;

      if (hasWeightedQuestions) {
        let weightedTotal = 0;
        let totalWeight = 0;

        analysisData.questions.forEach((question) => {
          if (question.weight_percentage > 0) {
            const answer = submission.answers.find((entry) => entry.question_id === question.id);
            if (answer) {
              const weight = question.weight_percentage / 100;
              weightedTotal += answer.score * weight;
              totalWeight += weight;
            }
          }
        });

        if (totalWeight > 0) {
          return weightedTotal / totalWeight;
        }
      }

      const scores = analysisData.questions
        .map((question) => submission.answers.find((entry) => entry.question_id === question.id)?.score)
        .filter((score): score is number => typeof score === "number");

      if (scores.length === 0) return 0;

      return scores.reduce((total, score) => total + score, 0) / scores.length;
    },
    [analysisData, hasWeightedQuestions],
  );

  const getScoreColor = useCallback((score: number) => {
    if (score >= 4) return "text-success";
    if (score >= 3) return "text-warning";
    return "text-destructive";
  }, []);

  const getScoreBadgeVariant = useCallback((score: number): ScoreVariant => {
    if (score >= 4) return "default";
    if (score >= 3) return "secondary";
    return "destructive";
  }, []);

  return { calculateSubmissionScore, getScoreColor, getScoreBadgeVariant } as const;
};

export const ScriptAnalysisDialog = ({ scriptForm, scriptName, script, open, onOpenChange }: ScriptAnalysisDialogProps) => {
  const { toast } = useToast();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { language } = useTranslation();
  const formDisplayName = language === "th" && scriptForm.feedback_forms.name_th
    ? scriptForm.feedback_forms.name_th
    : scriptForm.feedback_forms.name;

  // Version management
  const { data: versions } = useScriptVersions(script.id);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  const fallbackVersionId = scriptForm.script_version_id ?? (versions?.[0]?.id ?? "");
  const resolvedVersionId = selectedVersionId ?? fallbackVersionId;
  const activeVersionId = resolvedVersionId || (versions?.[0]?.id ?? "");
  const activeVersion = versions?.find((version) => version.id === activeVersionId);

  const { data: analysisData, isLoading, error, refresh } = useScriptAnalysis({ scriptFormId: open ? scriptForm.id : null });

  // Delete submission hook
  const { deleteSubmission, isDeleting } = useDeleteSubmission({
    toast,
    onSuccess: () => {
      void refresh();
    },
  });

  const share = useAnalysisShare({
    scriptName,
    formName: formDisplayName,
    reviewLink: scriptForm.review_link,
    toast,
  });

  useEffect(() => {
    if (error) {
      toast({
        title: "Error loading analysis",
        description: getErrorMessage(error, "Unable to load analysis"),
        variant: "destructive",
      });
    }
  }, [error, toast]);

  useEffect(() => {
    if (open) {
      void refresh();
    }
  }, [open, refresh]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const { calculateSubmissionScore, getScoreColor, getScoreBadgeVariant } = useSubmissionScoring(analysisData);

  const panelSide = isDesktop ? "right" : "bottom";

  const renderContent = () => {
    if (!analysisData || analysisData.submissions.length === 0) {
      return <AnalysisEmptyState />;
    }

    return (
      <AnalysisSummary
        analysisData={analysisData}
        scriptName={scriptName}
        formDisplayName={formDisplayName}
        formatDate={formatDate}
        getScoreColor={getScoreColor}
        getScoreBadgeVariant={getScoreBadgeVariant}
        calculateSubmissionScore={calculateSubmissionScore}
        deleteSubmission={deleteSubmission}
        isDeleting={isDeleting}
      />
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={panelSide}
        className="w-full max-h-[90vh] overflow-y-auto px-6 py-8 sm:max-w-6xl print:max-h-none print:overflow-visible print:p-0"
      >
        <SheetHeader className="space-y-4 pb-6 print:hidden">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <SheetTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5" />
                <span>
                  Analysis: {scriptName} - {formDisplayName}
                </span>
              </SheetTitle>
            </div>
            <VersionSelector
              versions={versions}
              activeVersion={activeVersion}
              activeVersionId={activeVersionId}
              currentVersionId={script.current_version_id}
              onChange={(value) => setSelectedVersionId(value)}
            />
            <ShareActions
              onPrint={share.handlePrint}
              onShare={share.handleShare}
              onCopyLink={share.handleCopyShareLink}
              onEmail={share.handleEmailShare}
              canUseNativeShare={share.canUseNativeShare}
            />
          </div>
        </SheetHeader>
        {isLoading && !analysisData ? (
          <div className="flex h-[400px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          renderContent()
        )}
      </SheetContent>
    </Sheet>
  );
};
